/**
 * Import Service
 * 
 * Provides functions to import data from CSV and JSON formats
 */

export interface ImportOptions {
  skipFirstRow?: boolean; // For CSV headers
  validateRequired?: boolean;
  requiredFields?: string[];
}

export interface ImportResult<T> {
  success: boolean;
  data: T[];
  errors: string[];
  warnings: string[];
  totalRows: number;
  importedRows: number;
  skippedRows: number;
}

// Parse CSV string to array of objects
export const parseCSV = (csvContent: string, options: ImportOptions = {}): any[] => {
  const lines = csvContent.split('\n').filter(line => line.trim());
  if (lines.length === 0) return [];
  
  const startIndex = options.skipFirstRow ? 1 : 0;
  const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
  
  const data: any[] = [];
  
  for (let i = startIndex; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;
    
    // Handle quoted values with commas
    const values: string[] = [];
    let currentValue = '';
    let inQuotes = false;
    
    for (let j = 0; j < line.length; j++) {
      const char = line[j];
      
      if (char === '"') {
        if (inQuotes && line[j + 1] === '"') {
          // Escaped quote
          currentValue += '"';
          j++;
        } else {
          // Toggle quote state
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        values.push(currentValue.trim());
        currentValue = '';
      } else {
        currentValue += char;
      }
    }
    values.push(currentValue.trim()); // Add last value
    
    // Map values to headers
    const row: any = {};
    headers.forEach((header, index) => {
      let value = values[index] || '';
      // Remove surrounding quotes if present
      value = value.replace(/^"|"$/g, '');
      // Convert empty strings to undefined for optional fields
      row[header] = value === '' ? undefined : value;
    });
    
    data.push(row);
  }
  
  return data;
};

// Parse JSON string to array of objects
export const parseJSON = (jsonContent: string): any[] => {
  try {
    const parsed = JSON.parse(jsonContent);
    // Handle both array and single object
    return Array.isArray(parsed) ? parsed : [parsed];
  } catch (error) {
    throw new Error(`Invalid JSON format: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

// Validate user data
const validateUserData = (user: any, index: number): { valid: boolean; errors: string[] } => {
  const errors: string[] = [];
  
  // Required fields
  if (!user.email || !user.email.trim()) {
    errors.push(`Row ${index + 1}: Email is required`);
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(user.email.trim())) {
    errors.push(`Row ${index + 1}: Invalid email format`);
  }
  
  if (!user.role) {
    errors.push(`Row ${index + 1}: Role is required`);
  } else if (!['customer', 'technician', 'admin'].includes(user.role.toLowerCase())) {
    errors.push(`Row ${index + 1}: Role must be one of: customer, technician, admin`);
  }
  
  // Optional but validate format if present
  if (user.phone && !/^[\d\s+()-]+$/.test(user.phone)) {
    errors.push(`Row ${index + 1}: Invalid phone format`);
  }
  
  return {
    valid: errors.length === 0,
    errors
  };
};

// Normalize user data from import (handle different column name variations)
const normalizeUserData = (row: any): any => {
  // Map various column name formats to our standard format
  const normalized: any = {
    email: row.email || row.Email || row['Email Address'] || row['E-mail'],
    firstName: row.firstName || row['First Name'] || row['First Name'] || row.firstname || row['First'],
    lastName: row.lastName || row['Last Name'] || row.lastname || row['Last'],
    role: (row.role || row.Role || row.role?.toLowerCase() || 'customer').toLowerCase(),
    phone: row.phone || row.Phone || row['Phone Number'] || row.phoneNumber || row['Phone'],
    isActive: row.isActive !== undefined ? row.isActive : 
              row.active !== undefined ? row.active :
              row['Active'] !== undefined ? row['Active'] :
              row['Is Active'] !== undefined ? row['Is Active'] :
              (row['Active'] === 'Yes' || row['Active'] === 'yes' || row['Active'] === 'Y' || row['Active'] === 'y' ? true :
               row['Active'] === 'No' || row['Active'] === 'no' || row['Active'] === 'N' || row['Active'] === 'n' ? false : true),
  };
  
  // Clean up role
  if (normalized.role && !['customer', 'technician', 'admin'].includes(normalized.role)) {
    normalized.role = 'customer'; // Default to customer if invalid
  }
  
  return normalized;
};

// Import users from CSV
export const importUsersFromCSV = (
  csvContent: string,
  options: ImportOptions = {}
): ImportResult<any> => {
  const result: ImportResult<any> = {
    success: true,
    data: [],
    errors: [],
    warnings: [],
    totalRows: 0,
    importedRows: 0,
    skippedRows: 0,
  };
  
  try {
    const parsed = parseCSV(csvContent, { skipFirstRow: true, ...options });
    result.totalRows = parsed.length;
    
    parsed.forEach((row, index) => {
      const normalized = normalizeUserData(row);
      const validation = validateUserData(normalized, index);
      
      if (validation.valid) {
        result.data.push(normalized);
        result.importedRows++;
      } else {
        result.errors.push(...validation.errors);
        result.skippedRows++;
      }
    });
    
    result.success = result.errors.length === 0;
  } catch (error) {
    result.success = false;
    result.errors.push(error instanceof Error ? error.message : 'Unknown error parsing CSV');
  }
  
  return result;
};

// Import users from JSON
export const importUsersFromJSON = (
  jsonContent: string,
  options: ImportOptions = {}
): ImportResult<any> => {
  const result: ImportResult<any> = {
    success: true,
    data: [],
    errors: [],
    warnings: [],
    totalRows: 0,
    importedRows: 0,
    skippedRows: 0,
  };
  
  try {
    const parsed = parseJSON(jsonContent);
    result.totalRows = parsed.length;
    
    parsed.forEach((row, index) => {
      const normalized = normalizeUserData(row);
      const validation = validateUserData(normalized, index);
      
      if (validation.valid) {
        result.data.push(normalized);
        result.importedRows++;
      } else {
        result.errors.push(...validation.errors);
        result.skippedRows++;
      }
    });
    
    result.success = result.errors.length === 0;
  } catch (error) {
    result.success = false;
    result.errors.push(error instanceof Error ? error.message : 'Unknown error parsing JSON');
  }
  
  return result;
};

// Read file content
export const readFileContent = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const content = e.target?.result as string;
      resolve(content);
    };
    
    reader.onerror = (e) => {
      reject(new Error('Failed to read file'));
    };
    
    reader.readAsText(file);
  });
};

// Detect file type from filename
export const detectFileType = (filename: string): 'csv' | 'json' | 'unknown' => {
  const ext = filename.toLowerCase().split('.').pop();
  if (ext === 'csv') return 'csv';
  if (ext === 'json') return 'json';
  return 'unknown';
};

